/**
 * Avatar — React (web) implementation.
 *
 * Renders a supplied image (cover) when `imageUri` is set, or the asset the
 * host registered under `imageAssetKey`, otherwise a placeholder circle with a
 * person glyph. All values come from tokens.
 */

/// <reference lib="dom" />

import React from 'react';
import { Icon } from '../Icon/Icon.web';
import { SkeletonBlock } from '../SkeletonBlock/SkeletonBlock.web';
import { resolveAvatarAsset } from './avatar-asset-registry';
import {
  AVATAR_BADGE_BACKGROUND,
  AVATAR_BADGE_TEXT_COLOR,
  AVATAR_BADGE_TYPOGRAPHY,
  AVATAR_DEFAULT_SIZE,
  AVATAR_GLYPH_RATIO,
  AVATAR_INITIALS_COLOR,
  avatarInitialsTypography,
  AVATAR_PLACEHOLDER_BACKGROUND,
  AVATAR_PLACEHOLDER_ICON,
  AVATAR_PLACEHOLDER_ICON_COLOR,
  avatarBadgeHeight,
} from './Avatar';

export interface WebAvatarProps {
  imageUri?: string;
  imageAssetKey?: string;
  initials?: string;
  label?: string;
  size?: number;
  badge?: string;
  'data-testid'?: string;
  fallback?: React.ReactNode;
}

export function Avatar({
  imageUri,
  imageAssetKey,
  initials,
  label,
  size = AVATAR_DEFAULT_SIZE,
  badge,
  fallback,
  'data-testid': testId,
}: WebAvatarProps) {
  const initialsType = avatarInitialsTypography(size);
  // Keyed by URL, so a replacement photo is tried afresh without a reset effect.
  const [failedUri, setFailedUri] = React.useState<string | null>(null);
  const [loadedUri, setLoadedUri] = React.useState<string | null>(null);
  const base: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: size / 2,
    flexShrink: 0,
    overflow: 'hidden',
  };

  // On web a registered asset is a `{ uri }` source; a bundler handle (number)
  // has no meaning here, so it degrades to the placeholder rather than
  // rendering a broken image. An unregistered key degrades the same way.
  const registered = imageAssetKey ? resolveAvatarAsset(imageAssetKey) : undefined;
  const registeredUri = typeof registered === 'object' ? registered.uri : undefined;
  const imageFailed = imageUri !== undefined && failedUri === imageUri;
  const src = imageUri ? (imageFailed ? undefined : imageUri) : registeredUri;

  // Remote photos have an explicit loading state; bundled assets retain their existing path.
  if (imageUri && !imageFailed) {
    const pending = loadedUri !== imageUri;
    return withBadge(
      <span
        role={label ? 'img' : undefined}
        aria-label={label}
        aria-hidden={label ? undefined : true}
        data-testid={badge ? undefined : testId}
        style={{ ...base, position: 'relative', display: 'inline-flex' }}
      >
        {pending && (
          <SkeletonBlock
            width={size}
            height={size}
            radius="rounded"
            testID={testId ? `${testId}-loading` : undefined}
          />
        )}
        <img
          src={imageUri}
          alt=""
          aria-hidden
          style={{
            ...base,
            position: 'absolute',
            inset: 0,
            objectFit: 'cover',
            opacity: pending ? 0 : 1,
          }}
          onLoad={() => setLoadedUri(imageUri)}
          onError={() => setFailedUri(imageUri)}
        />
      </span>,
      size,
      badge,
      testId,
    );
  }

  if (fallback != null) {
    const pending = !!src && loadedUri !== src;
    return withBadge(
      <span
        role={label ? 'img' : undefined}
        aria-label={label}
        aria-hidden={label ? undefined : true}
        data-testid={badge ? undefined : testId}
        style={{
          ...base,
          overflow: 'visible',
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {(!src || pending) && fallback}
        {src && (
          <img
            src={src}
            alt=""
            aria-hidden
            style={{
              ...base,
              position: 'absolute',
              inset: 0,
              objectFit: 'cover',
              opacity: pending ? 0 : 1,
            }}
            onLoad={() => setLoadedUri(src)}
            onError={imageUri ? () => setFailedUri(imageUri) : undefined}
          />
        )}
      </span>,
      size,
      badge,
      testId,
    );
  }

  if (src) {
    return withBadge(
      <img
        src={src}
        alt={label ?? ''}
        data-testid={badge ? undefined : testId}
        style={{ ...base, objectFit: 'cover' }}
        onError={imageUri ? () => setFailedUri(imageUri) : undefined}
      />,
      size,
      badge,
      testId,
    );
  }

  return withBadge(
    <span
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      data-testid={badge ? undefined : testId}
      style={{
        ...base,
        backgroundColor: AVATAR_PLACEHOLDER_BACKGROUND,
        display: 'flex',
        // Bottom-anchor the head/shoulders glyph so the circular clip trims the
        // shoulders (Figma) instead of centering it and leaving a gap below.
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
    >
      {initials ? (
        <span
          aria-hidden
          style={{
            alignSelf: 'center',
            color: AVATAR_INITIALS_COLOR,
            fontFamily: initialsType.fontFamily,
            fontSize: initialsType.fontSize,
            fontWeight: initialsType.fontWeight,
            lineHeight: `${initialsType.lineHeight}px`,
            letterSpacing: initialsType.letterSpacing,
          }}
        >
          {initials}
        </span>
      ) : (
        <Icon
          name={AVATAR_PLACEHOLDER_ICON}
          size={Math.round(size * AVATAR_GLYPH_RATIO)}
          color={AVATAR_PLACEHOLDER_ICON_COLOR}
          aria-hidden
        />
      )}
    </span>,
    size,
    badge,
    testId,
  );
}

/**
 * Wraps the circle with a corner badge when one is authored — the web mirror of
 * the native wrapper, so both registries render the same tree shape.
 */
function withBadge(
  circle: React.ReactElement,
  size: number,
  badge?: string,
  testId?: string,
): React.ReactElement {
  if (!badge) return circle;
  const height = avatarBadgeHeight(size);
  return (
    <span
      data-testid={testId}
      style={{ position: 'relative', display: 'inline-flex', width: size, height: size }}
    >
      {circle}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          top: -2,
          right: -2,
          height,
          width: height,
          borderRadius: height / 2,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: AVATAR_BADGE_BACKGROUND,
          color: AVATAR_BADGE_TEXT_COLOR,
          fontFamily: AVATAR_BADGE_TYPOGRAPHY.fontFamily,
          fontSize: AVATAR_BADGE_TYPOGRAPHY.fontSize,
          fontWeight: AVATAR_BADGE_TYPOGRAPHY.fontWeight,
          letterSpacing: AVATAR_BADGE_TYPOGRAPHY.letterSpacing,
        }}
      >
        {badge}
      </span>
    </span>
  );
}
